/**
 * Component to load the Table containing all of the Returns
 * within the main Returns-Dashboard.
 *
 * @component
 */

import { Badge, Button, Table, Tag, Tooltip } from 'antd';
import { ShrinkOutlined } from '@ant-design/icons';
import moment from 'moment';
import React from 'react';
import { IconTray } from '../../../../components/CustomReturnsIcons';
import { SubscriptionContext } from '../../../../contexts/SubscriptionContext';
import '../../../../styles/App.css';
import OrderDetails from './OrderDetails';

const typeColor = {
  return: {
    color: '#004085',
    backgroundColor: '##81ecec',
    borderColor: '#b8daff',
  },
  giftReturn: {
    width: '52px',
    textAlign: 'center',
    color: '#856404',
    backgroundColor: '#fdcb6e',
    borderColor: '#e17055',
  },
};

class OrdersTable extends React.Component {
  static contextType = SubscriptionContext;

  constructor(props) {
    super(props);
    this.state = {
      drawerVisible: false,
      orderNumber: null,
      orderObject: {},
      currentPage: 1,
      showIconToolTip: false,
      modalRecord: null,
    };
    this.resolutionOptions = {
      refundToOriginal: 'Refund',
      refundToExchange: 'Exchange',
      refundToCredit: 'Store credit',
      advancedExchange: 'Collection Exchange',
      multiple: 'Multiple',
      refundToCustom1: 'Custom 1',
      refundToCustom2: 'Custom 2',
      refundToCustom3: 'Custom 3',
      storeWideExchange: 'Store-Wide Exchange',
    };

    this.typeReturn = {
      return: 'Return',
      giftReturn: 'Gift',
    };

    this.statusColorMap = {
      Pending: 'orange',
      Approved: 'cyan',
      Rejected: 'magenta',
      'In Progress': 'blue',
    };

    this.columns = [
      {
        title: (
          <span
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            data-intercom-target="order-orderNumber-dashboard"
          >
            {' '}
            Order <ShrinkOutlined rotate={135} />{' '}
          </span>
        ),
        dataIndex: 'orderNumber',
        key: 'orderNumber',
        render: (text, record) => {
          return <span>#{record.orderNumber}</span>;
        },
        onHeaderCell: () => {
          return {
            onClick: () => this.props.handleSorter('orderNumber'),
          };
        },
        className: 'filter',
      },
      {
        // title: 'Total returns',
        title: (
          <span
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            data-intercom-target="order-totalReturn-dashboard"
          >
            {' '}
            Total returns <ShrinkOutlined rotate={135} />{' '}
          </span>
        ),
        dataIndex: 'totalReturns',
        key: 'totalReturns',
        render: (text, record) => {
          return (
            <span style={{ paddingLeft: '30px' }}>
              {record.returns?.length || 1}
            </span>
          );
        },
        onHeaderCell: () => {
          return {
            onClick: () => this.props.handleSorter('totalReturns'),
          };
        },
        className: 'filter',
      },
      {
        // title: 'Customer',
        title: (
          <span
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            data-intercom-target="order-customerName-dashboard"
          >
            {' '}
            Customer <ShrinkOutlined rotate={135} />{' '}
          </span>
        ),
        dataIndex: 'customerName',
        key: 'customerName',
        render: (text, record) => (
          <span style={{ textTransform: 'capitalize' }}>
            {record.customerName}
          </span>
        ),
        onHeaderCell: () => {
          return {
            onClick: () => this.props.handleSorter('customerLastName'),
          };
        },
        className: 'filter',
      },
      {
        // title: 'Order Date',
        title: (
          <span
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            data-intercom-target="order-orderDate-dashboard"
          >
            {' '}
            Order Date <ShrinkOutlined rotate={135} />{' '}
          </span>
        ),
        dataIndex: 'orderDate',
        key: 'orderDate',
        render: (text, record) => (
          <span> {moment(record.orderDate).format('MMM DD, YYYY')} </span>
        ),
        onHeaderCell: () => {
          return {
            onClick: () => this.props.handleSorter('orderDate'),
          };
        },
        className: 'filter',
      },
      {
        // title: 'Last request date',
        title: (
          <span
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            data-intercom-target="order-createdDate-dashboard"
          >
            {' '}
            Last request date <ShrinkOutlined rotate={135} />{' '}
          </span>
        ),
        dataIndex: 'lastCreatedAt',
        key: 'lastCreatedAt',
        render: (text, record) => (
          <span> {moment(record.lastCreatedAt).format('MMM DD, YYYY')} </span>
        ),
        onHeaderCell: () => {
          return {
            onClick: () => this.props.handleSorter('lastCreatedAt'),
          };
        },
        className: 'filter',
      },
      {
        title: (
          <span
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            data-intercom-target="order-action-dashboard"
          >
            {' '}
            Action{' '}
          </span>
        ),

        dataIndex: 'action',
        key: 'action',
        render: (text, record) => {
          return (
            <Button
              type="default"
              size="small"
              onClick={() => {
                this.setState({
                  drawerVisible: true,
                  orderNumber: record.orderNumber,
                  orderObject: record,
                });
              }}
              data-intercom-target="order-view-button-dashboard"
            >
              View
            </Button>
          );
        },
      },
    ];
  }

  triggerPagination = (page) => {
    const paginationObject = {
      current: page,
      pageSize: 10,
    };
    this.props.paginatePages(paginationObject);
    this.setState({
      currentPage: paginationObject.current,
    });
  };

  hideDrawer = () => {
    this.setState({
      drawerVisible: false,
    });
    if (!this.props.searchValue) {
      this.props.resetSearchResults(this.state.currentPage);
    } else if (this.props.searchValue) {
      this.props.searchTerm(this.props.searchValue);
    }
  };

  render() {
    return (
      <React.Fragment>
        <div>
          <div style={{ overflowX: 'auto' }}>
            <Table
              columns={this.columns}
              rowKey={(record) =>
                `${record.orderNumber}_${record.customerName || ''}`
              }
              dataSource={this.props.listOrderObjects}
              pagination={{
                total: this.props.orderNumbersCount,
                current: this.props.currentPage,
                pageSize: 10,
                onChange: (page) => this.triggerPagination(page),
              }}
              style={{ minWidth: '768px' }}
              locale={{
                emptyText: (
                  <div className="flex-row flex-middle-xxs flex-center-xxs">
                    <div className="flex-col-xxs-12">
                      <IconTray size="md" />
                    </div>
                    <div>
                      <p className="TextBody TextBody--xxxs">No Data</p>
                    </div>
                  </div>
                ),
              }}
            />
            {this.state.drawerVisible ? (
              <OrderDetails
                orderNumber={this.state.orderNumber}
                orderObject={this.state.orderObject}
                drawerVisible={this.state.drawerVisible}
                resolutionOptions={this.resolutionOptions}
                hideDrawer={this.hideDrawer.bind(this)}
              />
            ) : (
              ''
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default OrdersTable;
